<template>
  <div>
    <br />
    <h1 class="text-xs-center">Advanced: Reports</h1>

    <v-layout row wrap>
      <v-flex xs12 pa-2 class="text-xs-right">
        <v-btn color="primary" :disabled="apiInProgress" @click="requestDialog = true">
          Request Reports
        </v-btn>
      </v-flex>

      <v-flex xs12 pa-2>
        <v-card>
          <v-card-title>
            <h2>Status Summary</h2>
            <v-spacer />
            <v-btn icon :disabled="loadingSummary || apiInProgress" @click="loadSummary()">
              <v-icon>fal fa-sync</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex v-for="item in summaryCards" :key="item.key" xs12 sm6 md4 lg2 pa-1>
                <v-card>
                  <v-card-title class="subheading">{{ item.label }}</v-card-title>
                  <v-card-text>
                    <div><strong>Source:</strong> {{ item.source }}</div>
                    <div><strong>Aggregate:</strong> {{ item.aggregate }}</div>
                    <div><strong>Custom:</strong> {{ item.custom }}</div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 pa-2>
        <v-card>
          <v-card-title>
            <h2>Report Records</h2>
            <v-spacer />
            <v-btn
              icon
              :disabled="loadingRecords || apiInProgress"
              :loading="loadingRecords"
              @click="loadRecords()"
            >
              <v-icon>fal fa-sync</v-icon>
            </v-btn>
            <v-btn-toggle v-model="activeRecordType" mandatory @change="onRecordTypeChange">
              <v-btn flat value="source">Source</v-btn>
              <v-btn flat value="aggregate">Aggregate</v-btn>
              <v-btn flat value="custom">Custom</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md4 pa-1>
                <v-select
                  class="v-select--fixed-width"
                  v-model="filters.reportType"
                  :items="reportTypeOptions"
                  item-text="label"
                  item-value="value"
                  label="Filter Report Type"
                  clearable
                  :menu-props="{ offsetY: true }"
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-select
                  v-model="filters.reportStatus"
                  :items="statusOptions"
                  label="Filter Status"
                  clearable
                  :menu-props="{ offsetY: true }"
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-text-field
                  v-model="filters.categoryId"
                  label="Filter Category ID"
                  clearable
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1 v-if="activeRecordType === 'aggregate'">
                <v-select
                  v-model="filters.reportFidelity"
                  :items="fidelityOptions"
                  label="Filter Fidelity"
                  clearable
                  :menu-props="{ offsetY: true }"
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-text-field
                  v-model="filters.dateFrom"
                  label="Date Start"
                  type="date"
                  :append-icon="filters.dateFrom ? 'fal fa-times' : ''"
                  @click:append="filters.dateFrom = null; onFilterChange()"
                  @change="onFilterChange"
                />
              </v-flex>
              <v-flex xs12 md4 pa-1>
                <v-text-field
                  v-model="filters.dateTo"
                  label="Date End"
                  type="date"
                  :append-icon="filters.dateTo ? 'fal fa-times' : ''"
                  @click:append="filters.dateTo = null; onFilterChange()"
                  @change="onFilterChange"
                />
              </v-flex>
            </v-layout>
            <v-data-table
              :headers="tableHeaders"
              :items="records"
              :loading="loadingRecords"
              :pagination.sync="pagination"
              :total-items="totalRecords"
              :rows-per-page-items="[10, 25, 50, 100]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
            >
              <template v-slot:items="props">
                <tr class="report-row" @click="openDataDialog(props.item)">
                  <td>{{ props.item.reportType }}</td>
                  <td>{{ props.item.reportFidelity || 'day' }}</td>
                  <td>{{ props.item.reportStatus }}</td>
                  <td>{{ formatTimestamp(props.item.periodStart) }}</td>
                  <td>{{ formatTimestamp(props.item.periodEnd) }}</td>
                  <td>{{ props.item.category || '-' }}</td>
                  <td>{{ props.item.categoryId || '-' }}</td>
                  <td>{{ formatTimestamp(props.item.dateUpdated) }}</td>
                  <td class="text-xs-right">
                    <v-btn
                      icon
                      :disabled="loadingRecords || deletingRecordId !== null || apiInProgress"
                      :loading="isDeletingRecord(props.item)"
                      @click.stop="deleteReportRecord(props.item)"
                    >
                      <v-icon small>fa fa-trash-alt</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="requestDialog" max-width="1000">
      <v-card>
        <v-card-title>
          <span class="headline">Request Reports</span>
          <v-spacer />
          <v-btn icon :disabled="apiInProgress" @click="requestDialog = false">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 md4 pa-1>
              <v-select
                class="v-select--fixed-width"
                v-model="requestForm.reportType"
                :items="reportTypeOptions"
                item-text="label"
                item-value="value"
                label="Report Type"
                :menu-props="{ offsetY: true }"
                :disabled="apiInProgress"
                @change="onReportTypeChange"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-select
                v-model="requestForm.target"
                :items="targetOptions"
                label="Target Table"
                :menu-props="{ offsetY: true }"
                :disabled="apiInProgress || requestForm.updatePayout"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-select
                v-model="requestForm.reportFidelity"
                :items="fidelityOptions"
                label="Report Fidelity"
                :menu-props="{ offsetY: true }"
                :disabled="apiInProgress || requestForm.target !== 'custom' || requestForm.updatePayout"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <transportation-owner-autocomplete
                v-model="requestForm.transportationOwner"
                label="Account Owner"
                placeholder="Search account owner"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-text-field
                v-model="requestForm.category"
                label="Category"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md4 pa-1>
              <v-text-field
                v-model="requestForm.categoryId"
                label="Category ID"
                :disabled="apiInProgress"
              />
            </v-flex>
            <v-flex xs12 md3 pa-1>
              <v-switch
                v-model="requestForm.recalculate"
                label="Recalculate Existing"
                :disabled="apiInProgress || requestForm.updatePayout"
              />
            </v-flex>
            <v-flex xs12 md3 pa-1>
              <v-text-field
                label="Date Start (UTC)"
                type="date"
                v-model="requestForm.dateStart"
                :append-icon="requestForm.dateStart ? 'fal fa-times' : ''"
                :disabled="apiInProgress"
                hide-details
                @click:append="requestForm.dateStart = null"
              />
            </v-flex>
            <v-flex xs12 md3 pa-1>
              <v-text-field
                label="Date End (UTC)"
                type="date"
                v-model="requestForm.dateEnd"
                :append-icon="requestForm.dateEnd ? 'fal fa-times' : ''"
                :disabled="apiInProgress"
                hide-details
                @click:append="requestForm.dateEnd = null"
              />
            </v-flex>
            <v-flex xs12 md3 pa-1 v-if="isTicketRevenueRequest">
              <v-switch
                v-model="requestForm.updatePayout"
                label="Update Payout Only"
                :disabled="apiInProgress"
              />
            </v-flex>
          </v-layout>
          <div class="mt-2">
            <v-btn color="primary" :disabled="!canSubmitReportRequest" @click="submitReportRequest">Request Reports</v-btn>
            <v-btn flat :disabled="apiInProgress" @click="requestDialog = false">Cancel</v-btn>
          </div>
          <div v-if="requestResultMessage" class="mt-2">{{ requestResultMessage }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dataDialog" max-width="800">
      <v-card v-if="selectedRecord">
        <v-card-title>
          <span class="headline">Report Data</span>
          <v-spacer />
          <v-btn icon @click="dataDialog = false">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap class="mb-2">
            <v-flex xs12 sm6>
              <div><strong>Report Type:</strong> {{ selectedRecord.reportType }}</div>
              <div><strong>Fidelity:</strong> {{ selectedRecord.reportFidelity || 'day' }}</div>
              <div><strong>Status:</strong> {{ selectedRecord.reportStatus }}</div>
              <div><strong>Category:</strong> {{ selectedRecord.category || '-' }}</div>
            </v-flex>
            <v-flex xs12 sm6>
              <div><strong>Category ID:</strong> {{ selectedRecord.categoryId || '-' }}</div>
              <div><strong>Period:</strong> {{ formatTimestamp(selectedRecord.periodStart) }} &mdash; {{ formatTimestamp(selectedRecord.periodEnd) }}</div>
              <div><strong>Updated:</strong> {{ formatTimestamp(selectedRecord.dateUpdated) }}</div>
              <div v-if="selectedRecord.errorMessage"><strong>Error:</strong> <span class="red--text">{{ selectedRecord.errorMessage }}</span></div>
            </v-flex>
          </v-layout>

          <div class="subheading mb-1">Data</div>
          <div class="report-data-viewer">
            <json-viewer
              :value="selectedRecord.data || {}"
              :expand-depth="3"
              copyable
              sort
            />
          </div>

          <template v-if="selectedRecord.metadata">
            <div class="subheading mb-1 mt-3">Metadata</div>
            <div class="report-data-viewer">
              <json-viewer
                :value="selectedRecord.metadata || {}"
                :expand-depth="2"
                copyable
                sort
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import src from './src'
export default src
</script>

<style scoped>
.report-row {
  cursor: pointer;
}
.report-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.report-data-viewer {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
